/** JSON:API document shapes for the Codeverse API. */

export interface ResourceIdentifier {
  type: string
  id: string
}

export interface Relationship {
  data?: ResourceIdentifier | ResourceIdentifier[] | null
  links?: { related?: string; self?: string }
}

/** A resource as the server returns it. */
export interface Resource<Attributes = Record<string, unknown>> {
  id: string
  type: string
  attributes?: Attributes
  relationships?: Record<string, Relationship | undefined>
  links?: Record<string, string>
  meta?: Record<string, unknown>
}

/** A resource as the client sends it on create. The server assigns the id. */
export interface NewResource<Attributes = Record<string, unknown>> {
  type: string
  attributes?: Attributes
  relationships?: Record<string, Relationship>
}

/** A resource as the client sends it on update. JSON:API puts the id inside `data` as well. */
export interface ResourceUpdate<Attributes = Record<string, unknown>> {
  type: string
  id: string
  attributes?: Attributes
  relationships?: Record<string, Relationship>
}

export interface Document<Data = Resource> {
  data: Data
  included?: Resource[]
  meta?: Record<string, unknown>
}

export interface ApiErrorObject {
  status?: string
  code?: string
  title?: string
  detail?: string
  source?: { pointer?: string; parameter?: string }
}

export interface ErrorDocument {
  errors: ApiErrorObject[]
}

export interface AccessTokenAttributes {
  access_token: string
  refresh_token: string
  token_type: string
  scopes: string[]
  /** Remaining lifetime at the moment the response was generated. */
  expires_in_seconds: number
  /** UNIX timestamp — unlike every other created_at, which is ISO8601. */
  created_at: number
}

export type UserScope = 'account' | 'administrator' | 'child' | 'guide'

export interface UserAttributes {
  name: string
  email: string | null
  username: string
  referral_code: string
  scope: UserScope
  verified: boolean
  private_profile: boolean
  created_at: string
}

export type OrganizationClassification =
  | 'club'
  | 'guide'
  | 'household'
  | 'influencer'
  | 'internal'
  | 'school'

export interface OrganizationAttributes {
  name: string
  classification: OrganizationClassification
}

export type SubscriptionTier = 'digital' | 'kit'
export type SubscriptionCadence = 'monthly' | 'annual'
export type SubscriptionStatus = 'active' | 'paused' | 'cancelled'

export interface SubscriptionAttributes {
  tier: SubscriptionTier
  cadence: SubscriptionCadence
  status: SubscriptionStatus
  /**
   * The day Recharge will next charge, as YYYY-MM-DD. Read from Recharge for
   * one subscription at a time, so null in a list; null too once cancelled,
   * and until Recharge has reported the subscription.
   */
  next_charge_on: string | null
  /** The recurring price as the API formats it ("$34.95"), before tax and shipping; read with next_charge_on. */
  price: string | null
  next_coin_grant_at: string
  created_at: string
}

export interface ShippingAddressAttributes {
  first_name: string
  last_name: string
  company: string | null
  address1: string
  address2: string | null
  city: string
  province: string
  zip: string
  country_code: string
  phone: string | null
  /** Whether the subscription the address was read through ships to it. */
  current: boolean
}

/** Whether Recharge can charge a payment method. */
export type PaymentMethodStatus = 'valid' | 'invalid' | 'unvalidated' | 'empty'

export interface PaymentMethodAttributes {
  /** Such as CREDIT_CARD, PAYPAL or APPLE_PAY. */
  payment_type: string
  brand: string | null
  last4: string | null
  /** The last day the card can be charged, as YYYY-MM-DD; null for a method that does not expire. */
  expires_on: string | null
  status: PaymentMethodStatus | null
  status_reason: string | null
}

export const SESSIONS_TYPE = 'sessions'
export const ACCESS_TOKENS_TYPE = 'access_tokens'
export const USERS_TYPE = 'users'
export const PASSWORD_RESETS_TYPE = 'password_resets'
export const SUBSCRIPTIONS_TYPE = 'subscriptions'
export const SHIPPING_ADDRESSES_TYPE = 'shipping_addresses'
export const PAYMENT_METHODS_TYPE = 'payment_methods'
export const PAYMENT_METHOD_UPDATE_EMAILS_TYPE = 'payment_method_update_emails'
